import { Location } from '@angular/common';
import { DestroyRef, Injectable, NgZone, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { FeatureFlagService } from '@feature-flag';
import { AuthConfig, OAuthService } from 'angular-oauth2-oidc';
import { take } from 'rxjs';

import { environment } from '../../../environments/environment';
import { USER_SESSION } from '../core.module';

@Injectable({
  providedIn: 'root',
})
export class VismaOAuthService {
  private readonly oAuthService = inject(OAuthService);
  private readonly router = inject(Router);
  private readonly featureFlagService = inject(FeatureFlagService);
  private readonly userSession = inject(USER_SESSION);
  private readonly location = inject(Location);
  private readonly ngZone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);

  // Signals for update
  private readonly _accessTokenSignal = signal<string | null>(null);
  private readonly _isAuthenticatedSignal = signal<boolean>(false);
  private readonly _isAuthenticatingSignal = signal<boolean>(true);

  // Public readonly access to the signals
  public readonly accessTokenSignal = this._accessTokenSignal.asReadonly();
  public readonly isAuthenticatedSignal = this._isAuthenticatedSignal.asReadonly();
  public readonly isAuthenticatingSignal = this._isAuthenticatingSignal.asReadonly();

  constructor() {
    this.featureFlagService
      .getFlagValue('disable-ai-assistant')
      .pipe(take(1))
      .subscribe((isDisabled) => {
        if (isDisabled) {
          console.warn('Skipping OAuth initialization due to feature flag');
          this._isAuthenticatingSignal.set(false);
        } else if (environment.oauth.issuer === undefined) {
          console.warn('Skipping OAuth initialization due to environment settings');
          this._isAuthenticatingSignal.set(false);
        } else {
          this.configureOAuth();
          this.setupEventHandlers();
          this.setupCrossTabSync();
          this.initAuth().catch((error) => {
            console.error('OAuth initialization failed:', error);
            this._isAuthenticatingSignal.set(false);
          });
        }
      });
  }

  /**
   * Initialize OAuth authentication
   */
  private async initAuth(): Promise<void> {
    try {
      await this.oAuthService.loadDiscoveryDocument();

      // 1. Page reload (access token)
      if (this.oAuthService.hasValidAccessToken()) {
        return;
      }

      // 2. Loading after some time (refresh token)
      if (await this.tryRefreshToken()) {
        return;
      }

      // 3. Try login flow (use query params from redirect)
      const loginResult = await this.oAuthService.tryLogin();
      if (loginResult && this.oAuthService.hasValidAccessToken()) {
        return;
      }

      // Fallback: Init login (redirect to OAuth service)
      this.oAuthService.initCodeFlow(window.location.pathname);
    } finally {
      this.oAuthService.setupAutomaticSilentRefresh();
      this.validateUserSync();
      await this.handlePostLoginNavigation();

      this._isAuthenticatingSignal.set(false);
    }
  }

  /**
   * Attempt to refresh existing token
   */
  private async tryRefreshToken(): Promise<boolean> {
    const refreshToken = this.oAuthService.getRefreshToken();
    if (!refreshToken) {
      return false;
    }

    try {
      await this.oAuthService.refreshToken();
      return this.oAuthService.hasValidAccessToken();
    } catch {
      this.forceReAuthentication();
      return false;
    }
  }

  /**
   * Get current access token
   */
  public getAccessToken(): string | null {
    return this.oAuthService.getAccessToken();
  }

  /**
   * Configure OAuth with authorization code flow
   */
  private configureOAuth(): void {
    const authConfig: AuthConfig = {
      issuer: environment.oauth.issuer,
      clientId: environment.oauth.clientId,
      responseType: 'code',
      redirectUri: `${window.location.origin}`,
      scope: 'openid profile email offline_access vsn-assistant-api:chat',
      oidc: true,
      requireHttps: environment.type !== 'local',
      clearHashAfterLogin: true,
      clockSkewInSec: 300,
      preserveRequestedRoute: true,
      // Some randomization in timeout to avoid issues when multiple tabs are opened
      timeoutFactor: this.generateRandomTimeoutFactor(),
    };

    this.oAuthService.configure(authConfig);
    this.oAuthService.setStorage(localStorage);
  }

  /**
   * Handle OAuth events and update authentication state
   */
  private setupEventHandlers(): void {
    this.oAuthService.events.subscribe((event) => {
      switch (event.type) {
        case 'token_received':
        case 'token_refreshed':
        case 'silently_refreshed':
          this._accessTokenSignal.set(this.oAuthService.getAccessToken());
          this._isAuthenticatedSignal.set(this.oAuthService.hasValidAccessToken());
          break;
        case 'token_error':
          console.error('OAuth error:', event.type, event);
          this._isAuthenticatingSignal.set(false);
          break;
        case 'token_refresh_error':
        case 'silent_refresh_error':
          if (this.oAuthService.hasValidAccessToken()) {
            this._accessTokenSignal.set(this.oAuthService.getAccessToken());
            this._isAuthenticatedSignal.set(true);
          } else {
            this._accessTokenSignal.set(null);
            this._isAuthenticatedSignal.set(false);
          }
          break;
        case 'logout':
          this._accessTokenSignal.set(null);
          this._isAuthenticatedSignal.set(false);
          break;
      }
    });

    this._accessTokenSignal.set(this.oAuthService.getAccessToken());
    this._isAuthenticatedSignal.set(this.oAuthService.hasValidAccessToken());
  }

  /**
   * Sync signals when another tab refreshes tokens.
   * The storage event fires in all tabs except the one that made the change.
   */
  private setupCrossTabSync(): void {
    const handler = (event: StorageEvent): void => {
      if (event.key !== 'access_token') {
        return;
      }

      this.ngZone.run(() => {
        if (event.newValue) {
          this._accessTokenSignal.set(event.newValue);
          this._isAuthenticatedSignal.set(true);
        } else {
          this._accessTokenSignal.set(null);
          this._isAuthenticatedSignal.set(false);
        }
      });
    };

    this.ngZone.runOutsideAngular(() => {
      window.addEventListener('storage', handler);
    });

    this.destroyRef.onDestroy(() => {
      window.removeEventListener('storage', handler);
    });
  }

  /**
   * Handle navigation after successful login
   */
  private async handlePostLoginNavigation(): Promise<void> {
    this.cleanUrl();

    const state = this.oAuthService.state;

    if (state && state !== 'undefined' && state !== 'null') {
      const stateUrl = state.startsWith('/') ? state : decodeURIComponent(state);
      const normalizedUrl = this.location.normalize(stateUrl);
      await this.router.navigateByUrl(normalizedUrl);
    }
  }

  /**
   * Clean OAuth parameters from URL after successful login
   */
  private cleanUrl(): void {
    const url = new URL(window.location.href);
    const oauthParams = ['code', 'state', 'session_state', 'error', 'error_description', 'iss'];

    const hasOAuthParams = oauthParams.some((param) => url.searchParams.has(param));
    if (!hasOAuthParams) {
      return;
    }

    oauthParams.forEach((param) => url.searchParams.delete(param));

    window.history.replaceState({}, document.title, url.toString());
  }

  /**
   * Validate that OAuth user matches user authenticated by GSID (considered source of truth)
   * Forces re-authentication if users are out of sync, but with fallback to prevent infinite loops
   */
  private validateUserSync(): void {
    const claims = this.oAuthService.getIdentityClaims();

    if (
      claims?.email &&
      claims.email.toLowerCase().trim() !== this.userSession.emailAddress.toLowerCase().trim()
    ) {
      // Check if we've already tried to fix this recently to avoid infinite loops
      const lastMismatchTime = sessionStorage.getItem('oauth_user_mismatch_time');
      const now = Date.now();

      // 30 seconds grace period for re-authentication
      if (!lastMismatchTime || now - parseInt(lastMismatchTime) > 30000) {
        console.warn(
          `OAuth: User mismatch detected: OAuth(${claims.email}) vs System(${this.userSession.emailAddress})`,
        );

        sessionStorage.setItem('oauth_user_mismatch_time', now.toString());
        this.forceReAuthentication();
      } else {
        console.error(
          `OAuth: Persistent user mismatch detected: OAuth(${claims.email}) vs System(${this.userSession.emailAddress})`,
        );

        // Clear OAuth tokens completely - wrong user should not remain authenticated
        this.oAuthService.logOut(true);

        // Keep user logged out - don't attempt new authentication
        this._isAuthenticatedSignal.set(false);
        this._accessTokenSignal.set(null);
      }
    } else {
      // Clear any previous mismatch tracking on successful sync
      sessionStorage.removeItem('oauth_user_mismatch_time');
    }
  }

  /**
   * Force re-authentication by clearing tokens and starting new OAuth flow
   */
  private forceReAuthentication(): void {
    this._isAuthenticatingSignal.set(true);

    this.oAuthService.logOut(true);
    this.oAuthService.initCodeFlow(window.location.pathname);
  }

  /**
   * Generate random timeout factor
   * Workaround for https://github.com/manfredsteyer/angular-oauth2-oidc/issues/1148
   */
  private generateRandomTimeoutFactor(): number {
    const min = 0.65;
    const max = 0.85;
    const random = Math.random() * (max - min) + min;
    return Math.round(random * 1000) / 1000;
  }
}
